import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash, Battery, BatteryCharging } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface BatterySerialNumbersProps {
  installationId: number;
}

export function BatterySerialNumbers({ installationId }: BatterySerialNumbersProps) {
  const { toast } = useToast();
  // Start in adding mode by default when there are no batteries
  const [isAddingBattery, setIsAddingBattery] = useState(false);
  const [isEditingBattery, setIsEditingBattery] = useState<number | null>(null);
  const [newBattery, setNewBattery] = useState({
    serialNumber: '',
    model: '',
    capacity: '',
    notes: '',
  });

  // Fetch battery serial numbers
  const { data: batteries = [], isLoading } = useQuery<any[]>({
    queryKey: [`/api/installations/${installationId}/batteries`],
    enabled: !!installationId,
  });

  // Add new battery mutation
  const addBatteryMutation = useMutation({
    mutationFn: async (battery: any) => {
      return apiRequest({
        url: `/api/installations/${installationId}/batteries`,
        method: 'POST',
        data: battery,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${installationId}/batteries`],
      });
      setIsAddingBattery(false);
      setNewBattery({
        serialNumber: '',
        model: '',
        capacity: '',
        notes: '',
      });
      toast({
        title: 'Battery Added',
        description: 'The battery serial number has been added successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to add battery: ${error}`,
        variant: 'destructive',
      });
    },
  });

  // Update battery mutation
  const updateBatteryMutation = useMutation({
    mutationFn: async ({ id, battery }: { id: number; battery: any }) => {
      return apiRequest({
        url: `/api/installations/${installationId}/batteries/${id}`,
        method: 'PATCH',
        data: battery,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${installationId}/batteries`],
      });
      setIsEditingBattery(null);
      setNewBattery({
        serialNumber: '',
        model: '',
        capacity: '',
        notes: '',
      });
      toast({
        title: 'Battery Updated',
        description: 'The battery serial number has been updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update battery: ${error}`,
        variant: 'destructive',
      });
    },
  });

  // Delete battery mutation
  const deleteBatteryMutation = useMutation({
    mutationFn: async (batteryId: number) => {
      return apiRequest({
        url: `/api/installations/${installationId}/batteries/${batteryId}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${installationId}/batteries`],
      });
      toast({
        title: 'Battery Deleted',
        description: 'The battery serial number has been deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to delete battery: ${error}`,
        variant: 'destructive',
      });
    },
  });

  const handleAddBattery = () => {
    if (!newBattery.serialNumber) {
      toast({
        title: 'Validation Error',
        description: 'Serial number is required',
        variant: 'destructive',
      });
      return;
    }

    addBatteryMutation.mutate({
      installationId,
      serialNumber: newBattery.serialNumber,
      model: newBattery.model || null,
      capacity: newBattery.capacity || null,
      notes: newBattery.notes || null,
    });
  };

  const handleUpdateBattery = () => {
    if (!isEditingBattery || !newBattery.serialNumber) {
      return;
    }

    updateBatteryMutation.mutate({
      id: isEditingBattery,
      battery: {
        serialNumber: newBattery.serialNumber,
        model: newBattery.model || null,
        capacity: newBattery.capacity || null,
        notes: newBattery.notes || null,
      },
    });
  };

  const handleDeleteBattery = (batteryId: number) => {
    deleteBatteryMutation.mutate(batteryId);
  };

  const startEditing = (battery: any) => {
    setIsEditingBattery(battery.id);
    setNewBattery({
      serialNumber: battery.serialNumber,
      model: battery.model || '',
      capacity: battery.capacity || '',
      notes: battery.notes || '',
    });
  };

  const cancelAddOrEdit = () => {
    setIsAddingBattery(false);
    setIsEditingBattery(null);
    setNewBattery({
      serialNumber: '',
      model: '',
      capacity: '',
      notes: '',
    });
  };

  return (
    <div className="border rounded-lg p-4">
      <CardContent>
        {/* Battery list */}
        {isLoading ? (
          <div className="p-4 text-center">Loading batteries...</div>
        ) : batteries.length === 0 && !isAddingBattery ? (
          <div className="p-4 text-center border rounded space-y-2">
            <p className="text-muted-foreground">No batteries added yet</p>
            <div className="flex justify-center">
              <div className="p-3 rounded-md">
                <BatteryCharging className="h-10 w-10 text-muted mx-auto mb-2" />
                <p className="text-sm text-center">
                  Click "Add Battery" below to add battery details for this installation.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {batteries.map((battery: any) => (
              <div
                key={battery.id}
                className="border p-4 rounded flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2"
              >
                {isEditingBattery === battery.id ? (
                  <div className="w-full space-y-3">
                    <div>
                      <Label htmlFor="edit-serial">Serial Number*</Label>
                      <Input
                        id="edit-serial"
                        value={newBattery.serialNumber}
                        onChange={(e) =>
                          setNewBattery({ ...newBattery, serialNumber: e.target.value })
                        }
                        placeholder="Enter battery serial number"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="edit-model">Model</Label>
                        <Input
                          id="edit-model"
                          value={newBattery.model}
                          onChange={(e) => setNewBattery({ ...newBattery, model: e.target.value })}
                          placeholder="Battery model"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-capacity">Capacity</Label>
                        <Input
                          id="edit-capacity"
                          value={newBattery.capacity}
                          onChange={(e) =>
                            setNewBattery({ ...newBattery, capacity: e.target.value })
                          }
                          placeholder="Capacity (e.g. 5kWh)"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-notes">Notes</Label>
                      <Textarea
                        id="edit-notes"
                        value={newBattery.notes}
                        onChange={(e) => setNewBattery({ ...newBattery, notes: e.target.value })}
                        placeholder="Additional notes about this battery"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" onClick={cancelAddOrEdit}>
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateBattery}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <Battery className="h-4 w-4 text-primary" />
                        <span className="font-medium">{battery.serialNumber}</span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        {battery.model && <span>Model: {battery.model}</span>}
                        {battery.capacity && <span>Capacity: {battery.capacity}</span>}
                      </div>
                      {battery.notes && <p className="text-sm mt-1">{battery.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <Button variant="ghost" size="icon" onClick={() => startEditing(battery)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete the battery serial number record.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteBattery(battery.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add new battery form */}
        {isAddingBattery ? (
          <div className="mt-4 border p-4 rounded space-y-3">
            <h4 className="font-medium">Add New Battery</h4>
            <div>
              <Label htmlFor="new-serial">Serial Number*</Label>
              <Input
                id="new-serial"
                value={newBattery.serialNumber}
                onChange={(e) => setNewBattery({ ...newBattery, serialNumber: e.target.value })}
                placeholder="Enter battery serial number"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="new-model">Model</Label>
                <Input
                  id="new-model"
                  value={newBattery.model}
                  onChange={(e) => setNewBattery({ ...newBattery, model: e.target.value })}
                  placeholder="Battery model"
                />
              </div>
              <div>
                <Label htmlFor="new-capacity">Capacity</Label>
                <Input
                  id="new-capacity"
                  value={newBattery.capacity}
                  onChange={(e) => setNewBattery({ ...newBattery, capacity: e.target.value })}
                  placeholder="Capacity (e.g. 5kWh)"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new-notes">Notes</Label>
              <Textarea
                id="new-notes"
                value={newBattery.notes}
                onChange={(e) => setNewBattery({ ...newBattery, notes: e.target.value })}
                placeholder="Additional notes about this battery"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={cancelAddOrEdit}>
                Cancel
              </Button>
              <Button onClick={handleAddBattery}>Add Battery</Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center">
            <Button
              className="gap-2 px-8 py-6"
              onClick={() => setIsAddingBattery(true)}
              disabled={isEditingBattery !== null}
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">Add Battery</span>
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Click to add more battery serial numbers to this installation
            </p>
          </div>
        )}
      </CardContent>
    </div>
  );
}
